// File > Import CSD — frontend half of the C++ OnApp_import_csd_button →
// Ti_import_csd_file flow: pick a file, ship it to the import daemon via the
// import_csd_file command (progress dialog up while it runs), then show the
// daemon's verdict. Every displayed string comes from the resource table (the
// C++ resources where one exists, port-local ids for the success/failure lines).
//
// Web build: the file lives on the user's machine, so the browser's own file
// picker reads it and POSTs the bytes to /api/import_csd (the webserver then
// runs the same daemon round-trip). Results use the browser alert the other
// web flows use, since the Tauri message dialog isn't available there.

import { getTigerResource } from "./tigerResource";
import { PickerKey, pickerInitialDir, rememberPickerDir } from "./pickerFolder";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

/** Map the backend's failure text to what the user sees: the
 *  `TIGER_IMPORT_MODIF_REQ_FAIL` marker means no import daemon answered. */
function importErrorText(e: unknown): string {
  const text = String(e);
  return text === "TIGER_IMPORT_MODIF_REQ_FAIL" ? getTigerResource("TIGER_IMPORT_MODIF_REQ_FAIL") : text;
}

/** Browser file picker: resolves to the chosen File, or null when cancelled. */
export function pickWebFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.style.display = "none";
    let settled = false;
    const finish = (file: File | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(file);
    };
    input.addEventListener("change", () => finish(input.files?.[0] ?? null));
    // Browsers fire `cancel` on dismissal (Chromium/Firefox); fall back to a
    // focus check for those that don't.
    input.addEventListener("cancel", () => finish(null));
    window.addEventListener(
      "focus",
      () => setTimeout(() => finish(input.files?.[0] ?? null), 500),
      { once: true },
    );
    document.body.appendChild(input);
    input.click();
  });
}

/** Whole-file bytes; `Blob.arrayBuffer` where available, FileReader otherwise
 *  (older WebViews, and jsdom in tests). */
function readFileBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Import CSD data failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

/** Upload `file` to the webserver's Import CSD route. Resolves to the daemon's
 *  verdict; rejects with the backend's error text. */
export async function importCsdWeb(file: File): Promise<boolean> {
  const body = await readFileBytes(file);
  const res = await fetch(`/api/import_csd?filename=${encodeURIComponent(file.name)}`, {
    method: "POST",
    headers: { "Content-Type": "application/octet-stream" },
    body,
  });
  if (!res.ok) {
    throw new Error((await res.text()) || `Import CSD failed: ${res.status}`);
  }
  return Boolean(await res.json());
}

export async function importCsdFile(onBusy?: (busy: boolean) => void): Promise<void> {
  if (!isTauri()) {
    const file = await pickWebFile();
    if (!file) return; // cancelled
    onBusy?.(true);
    let imported: boolean;
    try {
      imported = await importCsdWeb(file);
    } catch (e) {
      onBusy?.(false);
      globalThis.alert(`${getTigerResource("TIGER_IMPORT_MODIF_TITLE")}\n\n${importErrorText(e)}`);
      return;
    }
    onBusy?.(false);
    globalThis.alert(
      imported
        ? getTigerResource("TIGER_IMPORT_CSD_SUCCESS")
        : `${getTigerResource("TIGER_IMPORT_MODIF_TITLE")}\n\n${getTigerResource("TIGER_IMPORT_CSD_FAIL")}`,
    );
    return;
  }

  const { open, message } = await import("@tauri-apps/plugin-dialog");
  const { invoke } = await import("@tauri-apps/api/core");

  const filePath = await open({
    title: getTigerResource("TIGER_IMPORT_CSD_TITLE"),
    multiple: false,
    directory: false,
    defaultPath: await pickerInitialDir(PickerKey.importCsd),
    filters: [{ name: getTigerResource("TIGER_EXPORT_FILTER_ALL"), extensions: ["*"] }],
  });
  if (!filePath || typeof filePath !== "string") return; // cancelled
  rememberPickerDir(PickerKey.importCsd, filePath);

  onBusy?.(true);
  let imported: boolean;
  try {
    imported = await invoke<boolean>("import_csd_file", { filePath });
  } catch (e) {
    onBusy?.(false);
    await message(importErrorText(e), { title: getTigerResource("TIGER_IMPORT_MODIF_TITLE"), kind: "error" });
    return;
  }
  onBusy?.(false);

  if (imported) {
    await message(getTigerResource("TIGER_IMPORT_CSD_SUCCESS"), {
      title: getTigerResource("TIGER_IMPORT_SAVE_TITLE"),
      kind: "info",
    });
  } else {
    await message(getTigerResource("TIGER_IMPORT_CSD_FAIL"), {
      title: getTigerResource("TIGER_IMPORT_MODIF_TITLE"),
      kind: "error",
    });
  }
}
